
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LegacyJob, DEPARTMENT_KEY_MAP, JOB_TYPE_KEY_MAP, JobPriority } from '../types';
import { formatRelativeTime } from '../src/utils/dateFormatter';
import { useCities } from '../src/contexts/CitiesContext';
import { isRemoteWorkStyle, isHomeBasedWorkStyle, isHybridWorkStyle } from '../src/utils/workStyleUtils';

// 使用 LegacyJob 类型用于向后兼容
type Job = LegacyJob;

interface JobRowProps {
  job: Job;
  onSelect?: (job: Job) => void;
}

const JobRow: React.FC<JobRowProps> = ({ job, onSelect }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const { citiesMap } = useCities();

  // 使用映射表获取部门翻译
  const getDepartmentTranslation = (dept: string) => {
    const key = DEPARTMENT_KEY_MAP[dept];
    if (!key) return dept;
    return t(`jobData.departments.${key}`, dept);
  };

  // 使用映射表获取职位类型翻译
  const getJobTypeTranslation = (type: string) => {
    const key = JOB_TYPE_KEY_MAP[type as keyof typeof JOB_TYPE_KEY_MAP];
    if (!key) return type;
    return t(`jobData.jobTypes.${key}`, type);
  };

  // 获取工作模式翻译
  const getWorkStyleTranslation = (workStyle: string) => {
    const key = workStyle === 'HOME_BASED' ? 'homeBased' :
                workStyle === 'REMOTE' ? 'remote' : 
                workStyle === 'HYBRID' ? 'hybrid' : 'onsite';
    return t(`jobData.workStyles.${key}`);
  };

  // 获取国家翻译
  const getCountryTranslation = (country: string) => {
    const countryMap: Record<string, string> = {
      'China': 'china',
      'UK': 'uk',
      'USA': 'usa',
      'India': 'india',
      'Vietnam': 'vietnam',
      'Malaysia': 'malaysia',
      'Singapore': 'singapore',
      'Thailand': 'thailand',
      'Indonesia': 'indonesia',
      'South Korea': 'southKorea'
    };
    const key = countryMap[country];
    if (!key) return country;
    return t(`jobData.countries.${key}`, country);
  };

  // 获取优先级样式和标签（只显示紧急）
  const getPriorityBadge = (priority?: JobPriority) => {
    if (priority !== 'HIGH') return null;

    return (
      <span className="shrink-0 inline-flex items-center text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border bg-red-100 text-red-600 border-red-200 animate-pulse">
        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
        </svg>
        {t('jobData.priorities.high')}
      </span>
    );
  };

  // 根据 cities 数组生成位置显示（优先使用结构化数据）
  const formatLocationFromCities = (cities: string[] | null | undefined, country: string | null | undefined) => {
    if (!cities || cities.length === 0) {
      return null;
    }

    const cityNames = cities.map(cityId => {
      const cityData = citiesMap.get(cityId);
      return cityData
        ? (currentLang.startsWith('zh') ? cityData.nameZh : cityData.nameEn)
        : cityId;
    }).join('/');

    if (country) {
      return `${cityNames}, ${getCountryTranslation(country)}`;
    }
    return cityNames;
  };

  // 格式化位置信息（将城市名和国家名翻译）
  const formatLocation = (location: string) => {
    // 处理特殊的全球远程位置
    if (location === 'Remote (Global)') {
      return t('jobData.locations.remoteGlobal', location);
    }

    // 处理 "City/Remote" 格式（如 "Tianjin/Remote"）
    if (location.includes('/Remote')) {
      const cityPart = location.replace('/Remote', '');
      const cityData = [...citiesMap.values()].find(c => cityPart === c.nameZh || cityPart === c.nameEn);
      const cityName = cityData
        ? (currentLang.startsWith('zh') ? cityData.nameZh : cityData.nameEn)
        : cityPart;
      const remoteText = t('jobData.locations.remote', 'Remote');
      return `${cityName}/${remoteText}`;
    }

    // 分割国家和城市部分
    const parts = location.split(', ');
    let countryName = '';

    // 如果有国家部分
    if (parts.length >= 2) {
      countryName = getCountryTranslation(parts[parts.length - 1]);
    }

    // 处理城市部分（可能包含多个城市用 / 分隔）
    const cityPart = parts[0] || location;
    const cityNames = cityPart.split('/').map(city => {
      const trimmedCity = city.trim();
      const cityData = [...citiesMap.values()].find(c => trimmedCity === c.nameZh || trimmedCity === c.nameEn);
      return cityData
        ? (currentLang.startsWith('zh') ? cityData.nameZh : cityData.nameEn)
        : trimmedCity;
    }).join('/');

    // 如果有国家，返回 "城市/城市, 国家" 格式
    if (parts.length >= 2) {
      return `${cityNames}, ${countryName}`;
    }

    // 处理只有城市名的情况（如 "Singapore"）
    return cityNames;
  };

  // 获取显示位置：优先使用 cities 数组，fallback 到 location 字段
  const getDisplayLocation = () => {
    const fromCities = formatLocationFromCities(job.cities, job.country);
    if (fromCities) {
      return fromCities;
    }
    return formatLocation(job.location);
  };

  return (
    <div
      onClick={() => onSelect?.(job)}
      className="group bg-white border-b border-slate-100 last:border-b-0 hover:bg-slate-50 hover:-translate-y-px hover:shadow-sm active:bg-slate-100 active:shadow-none transition-all duration-200 cursor-pointer"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-5 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 mb-1.5 sm:mb-2">
            <h3 className="text-base sm:text-lg font-bold text-slate-900 sm:truncate group-hover:text-blue-600 transition-colors break-words">
              {job.title}
            </h3>
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="shrink-0 text-[10px] font-bold uppercase tracking-widest px-1.5 sm:px-2 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100">
                {getDepartmentTranslation(job.department)}
              </span>
              {getPriorityBadge(job.priority)}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 sm:gap-x-3 md:gap-x-4 gap-y-1.5 sm:gap-y-2 text-xs sm:text-sm text-slate-500 font-medium">
            <div className="flex items-center gap-1 sm:gap-1.5 min-w-0 max-w-[60%]" title={getDisplayLocation()}>
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              <span className="truncate">{getDisplayLocation()}</span>
            </div>

            {/* Work Style Badge */}
            <div className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-bold ${
              isRemoteWorkStyle(job.workStyle) ? 'bg-purple-100 text-purple-700' :
              isHomeBasedWorkStyle(job.workStyle) ? 'bg-green-100 text-green-700' :
              isHybridWorkStyle(job.workStyle) ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
            }`}>
              {isRemoteWorkStyle(job.workStyle) ? (
                <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : isHomeBasedWorkStyle(job.workStyle) ? (
                <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              ) : isHybridWorkStyle(job.workStyle) ? (
                <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              ) : (
                <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
              <span>{getWorkStyleTranslation(job.workStyle)}</span>
            </div>

            <div className="flex items-center gap-1 sm:gap-1.5">
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{getJobTypeTranslation(job.type)}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between md:justify-end gap-2 sm:gap-3 md:gap-8 shrink-0">
          {/* 发布时间 - 移动端显示简短版本 */}
          <div className="hidden sm:hidden text-right">
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t('jobData.common.posted')}</div>
            <div className="text-xs font-semibold text-slate-500">
              {formatRelativeTime(job.postedDate, i18n.language)}
            </div>
          </div>
          <div className="hidden lg:block text-right">
            <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">{t('jobData.common.posted')}</div>
            <div className="text-sm font-semibold text-slate-600">
              {formatRelativeTime(job.postedDate, i18n.language)}
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 ml-auto">
            <button className="btn-safe-touch bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 active:scale-95 px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm transition-all shadow-sm hover:shadow-md cursor-pointer">
              {t('jobData.common.applyNow')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobRow;
